import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BellIcon, SpeakerWaveIcon, SpeakerXMarkIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { getLeadSoundEnabled, setLeadSoundEnabled } from '../hooks/useLeadNotifications';

export function NotificationCenter({
  notifications = [],
  unreadCount = 0,
  onOpen,
  onDismiss,
  onClearAll,
  onNotificationClick,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(getLeadSoundEnabled);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = () => {
    setOpen((v) => {
      if (!v && onOpen) onOpen();
      return !v;
    });
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    const next = !soundOn;
    setLeadSoundEnabled(next);
    setSoundOn(next);
    window.dispatchEvent(new Event('lead-sound-toggle'));
  };

  const handleDismiss = useCallback((e, id) => {
    e.stopPropagation();
    if (onDismiss && id != null) onDismiss(id);
  }, [onDismiss]);

  const handleClearAll = useCallback((e) => {
    e.stopPropagation();
    if (onClearAll) onClearAll();
  }, [onClearAll]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        className="relative p-2 rounded-lg transition-colors"
        style={{ color: unreadCount > 0 ? 'var(--primary)' : 'var(--muted)' }}
        title={t('notifications.title')}
      >
        <BellIcon className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1 w-80 rounded-lg shadow-lg border z-50 overflow-hidden"
          style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
            <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{t('notifications.leads')}</span>
            <div className="flex items-center gap-1">
              {/* Clear-all button: only visible when there are notifications */}
              {notifications.length > 0 && onClearAll && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="p-1 rounded transition-colors text-xs flex items-center gap-0.5"
                  style={{ color: 'var(--muted)' }}
                  title={t('notifications.clearAll')}
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                  <span className="text-[11px]">{t('notifications.clearAll')}</span>
                </button>
              )}
              {/* Sound toggle */}
              <button
                type="button"
                onClick={toggleSound}
                className="p-1 rounded transition-colors"
                style={{ color: soundOn ? 'var(--primary)' : 'var(--muted)' }}
                title={soundOn ? t('notifications.mute') : t('notifications.unmute')}
              >
                {soundOn ? <SpeakerWaveIcon className="h-4 w-4" /> : <SpeakerXMarkIcon className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Notification list */}
          <ul className="max-h-72 overflow-y-auto divide-y" style={{ borderColor: 'var(--border)' }}>
            {notifications.length === 0 ? (
              <li className="px-3 py-4 text-sm text-center" style={{ color: 'var(--muted)' }}>
                {t('notifications.empty')}
              </li>
            ) : (
              notifications.map((n, i) => (
                <li
                  key={n.id != null ? `${n.id}__${n.ts ?? i}` : `idx-${i}`}
                  className={`flex items-start gap-2 px-3 py-2.5 group${onNotificationClick ? ' cursor-pointer hover:bg-hover' : ''}`}
                  onClick={onNotificationClick ? () => { onNotificationClick(n); setOpen(false); } : undefined}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm" style={{ color: 'var(--foreground)' }}>{n.text}</p>
                    {n.created_at && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
                        {new Date(n.created_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                  {/* Per-item dismiss button */}
                  {onDismiss && n.id != null && (
                    <button
                      type="button"
                      onClick={(e) => handleDismiss(e, n.id)}
                      className="flex-shrink-0 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ color: 'var(--muted)' }}
                      title={t('notifications.dismiss')}
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default NotificationCenter;
